// Symptom explainers — friendly, Ayurveda-informed short reads.
// Used in the quiz results view, admin drill-downs, and Vaidya triage.

export const SYMPTOM_LIBRARY = {
    SYM_RED_FLAG: {
        name: "Red flag: seek immediate care",
        short: "This isn't an Ayurveda-first case. Please see a medical doctor now.",
        dosha_view:
            "Any dosha can be in a crisis state — the classical texts describe this as an 'avastha' where samples of prana are threatened. Home remedies wait until you are stable.",
        quick_tip:
            "Get to a hospital or emergency line. Bring a list of medicines and known conditions.",
        color: "error",
    },
    SYM_DIGESTION: {
        name: "General digestive imbalance",
        short: "Your agni (digestive fire) is asking for a reset.",
        dosha_view:
            "Vata cools agni, Pitta over-heats it, Kapha slows it. Restoring 'sama agni' is step one before any deeper protocol.",
        quick_tip:
            "Warm water sips, ginger tea 15 min before meals, no snacking between meals for 3 days.",
    },
    SYM_BLOATING: {
        name: "Bloating & gas",
        short: "Wind element (Vata) is disturbed in the belly.",
        dosha_view:
            "In Ayurveda bloating is 'aadhmana' — trapped Vata in the colon. Cold, dry, or raw foods and irregular eating worsen it.",
        quick_tip:
            "Warm cooked meals, hing (asafoetida) in dal, gentle abdominal massage clockwise with sesame oil.",
    },
    SYM_ACIDITY: {
        name: "Acidity, burning, heartburn",
        short: "Pitta is running hot in your stomach.",
        dosha_view:
            "Classical 'amla-pitta' — sour Pitta rising up. Spicy, fried, sour, and fermented foods stoke the fire.",
        quick_tip:
            "Soaked raisins in the morning, coconut water, avoid coffee on empty stomach, dinner before 8 pm.",
    },
    SYM_CONSTIPATION: {
        name: "Constipation",
        short: "Dry Vata is holding the bowels.",
        dosha_view:
            "Classical 'vibandha' — dry, cool Vata drying stools. Dehydration and late nights make this worse.",
        quick_tip:
            "1 tsp ghee in warm water at bedtime, triphala churna at night, hydrate with warm water through the day.",
    },
    SYM_LOOSE: {
        name: "Loose or urgent stools",
        short: "Pitta or Vata is pushing things through too fast.",
        dosha_view:
            "'Atisara' — imbalanced digestion. Common when agni is weak (Kapha), inflamed (Pitta) or erratic (Vata).",
        quick_tip:
            "Rice conjee with pomegranate, avoid raw salads for a few days, buttermilk with cumin.",
    },
    SYM_IRREGULAR: {
        name: "Alternating bowels",
        short: "Vata is oscillating — classic IBS-style pattern.",
        dosha_view:
            "'Grahani' imbalance. The small intestine loses its rhythm, often triggered by stress and irregular meals.",
        quick_tip:
            "Same meal times daily, 10-minute walk after dinner, buttermilk with ajwain + rock salt.",
    },
    SYM_ABD_PAIN: {
        name: "Abdominal pain",
        short: "Deserves a Vaidya's ear — could be Vata, Pitta or a red flag.",
        dosha_view:
            "'Shoola' — pain caused by Vata trapped by ama (undigested residue). Persistent or severe pain is NOT for self-treatment.",
        quick_tip:
            "If mild: warm compress, ajwain water. If severe or worsening — please see a medical doctor today.",
    },
    SYM_POSTMEAL: {
        name: "Post-meal worsening",
        short: "Agni can't handle the current load.",
        dosha_view:
            "When symptoms peak after eating, agni is either overwhelmed or already inflamed. Smaller, warmer meals give it space to recover.",
        quick_tip:
            "Halve portion sizes for 3 days, chew slowly, no water within 30 min of a meal.",
    },
    SYM_SLEEP: {
        name: "Sleep trouble",
        short: "Vata is racing at night or Kapha is heavy in the morning.",
        dosha_view:
            "'Anidra' — sleep imbalance. Vata insomnia is a busy mind at 2–4 am; Pitta insomnia is a hot, dream-filled 10 pm–2 am; Kapha lethargy is a heavy morning.",
        quick_tip:
            "Warm milk with nutmeg 30 min before bed, no screens after 10 pm, oil the soles of your feet.",
    },
    SYM_STRESS: {
        name: "Stress & anxiety",
        short: "Vata is up in the mind — grounding is the antidote.",
        dosha_view:
            "'Chittodvega' — anxious mind. Vata and Sadhaka Pitta are aggravated; the nervous system is running hot and dry.",
        quick_tip:
            "Nasya (2 drops of warm sesame oil in each nostril), Nadi Shodhana breathing 5 min, ashwagandha 500mg at night.",
    },
    SYM_SKIN: {
        name: "Skin or hair concerns",
        short: "Skin is a mirror of the gut and the liver.",
        dosha_view:
            "Ayurveda sees skin as 'rasa' and 'rakta' quality made visible. Digestive fire and liver detoxification lead every skin story.",
        quick_tip:
            "Coriander-cumin-fennel (CCF) tea daily, no cold water in the morning, gentle oil abhyanga before bath.",
    },
    SYM_METAB: {
        name: "Weight & metabolism",
        short: "Kapha or agni is out of rhythm.",
        dosha_view:
            "Excess weight is 'medoroga' — Kapha and slow agni. Under-weight is often Vata's dry, mobile nature and stress.",
        quick_tip:
            "Warm cooked breakfast, 3 meals no snacks, brisk 30-min walk, dinner light and early.",
    },
    SYM_IMMUNE: {
        name: "Immunity",
        short: "Ojas (vitality) needs replenishing.",
        dosha_view:
            "'Ojas' is the subtlest byproduct of good digestion. Frequent colds, low energy, and slow recovery signal depleted ojas.",
        quick_tip:
            "Chyawanprash 1 tsp daily, 7.5 hours of sleep, warm rasayana milk 3× a week.",
    },
    SYM_JOINT: {
        name: "Joint or muscle pain",
        short: "Vata is drying the joints; sometimes ama is lodged too.",
        dosha_view:
            "'Sandhivata' or 'aamavata' — the joints are dry, cold, or clogged. Damp weather and cold foods make this worse.",
        quick_tip:
            "Warm sesame oil massage, ginger + turmeric tea, avoid cold drinks, gentle daily movement.",
    },
};

export function describeSymptom(sid) {
    if (!sid) return null;
    return (
        SYMPTOM_LIBRARY[sid] || {
            name: sid.replace(/^SYM_/, "").replace(/_/g, " ").toLowerCase(),
            short: "",
            dosha_view: "",
            quick_tip: "",
        }
    );
}
